// src/controllers/post.controller.js
import { isAdminOrEditor } from '../services/permission.service'
import { postService } from '../services/PostSystem'
import { userService } from '../services/UserSystem'
import { utilService } from '../services/util.service'
import { socketService } from '../services/socket.service'

export const postController = {
  fetchPosts,
  fetchPostById,
  createPost,
  editPost,
  removePost,
  toggleLike,
  updateHelpful,
}

// --------------------------------------------------
// 1. LIST / DETAILS
// --------------------------------------------------
async function fetchPosts(filterBy = {}) {
  try {
    return await postService.query(filterBy)
  } catch (err) {
    console.error('Error in fetchPosts:', err)
    throw err
  }
}

async function fetchPostById(postId) {
  try {
    const post = await postService.getById(postId)
    if (!post) throw new Error(`Post with id ${postId} not found`)

    // enrich author.posts if exists
    const author = await userService
      .getById(post.author.userId)
      .catch(() => null)
    if (author) post.author.posts = author.posts || []

    return post
  } catch (err) {
    console.error('Error in fetchPostById:', err)
    throw err
  }
}

// --------------------------------------------------
// 2. CREATE / EDIT / REMOVE
// --------------------------------------------------
async function createPost(postData, loggedInUser) {
  try {
    if (!loggedInUser) throw new Error('Must be logged in to create a post')
    if (!isAdminOrEditor(loggedInUser)) {
      throw new Error(
        'Permission denied – only admins or editors may create posts'
      )
    }

    const { userId } = postData.author || {}
    if (!userId) throw new Error('author.userId is required')

    const toSave = {
      ...postService.getEmptyPost(),
      ...postData,
      author: { userId },
      createdAt: new Date().toISOString(),
    }

    const savedPost = await postService.save(toSave)
    socketService.emit('post-added', savedPost)
    return savedPost
  } catch (err) {
    console.error('Error in createPost:', err)
    throw err
  }
}

async function editPost(postId, updatedData, loggedInUser) {
  try {
    if (!loggedInUser) throw new Error('Must be logged in to edit a post')
    if (!isAdminOrEditor(loggedInUser)) {
      throw new Error(
        'Permission denied – only admins or editors may edit posts'
      )
    }

    const existing = await postService.getById(postId)
    if (!existing) throw new Error(`Post with id ${postId} not found`)

    const merged = { ...existing, ...updatedData, _id: postId }
    const savedPost = await postService.save(merged)

    socketService.emit('post-updated', savedPost)
    return savedPost
  } catch (err) {
    console.error('Error in editPost:', err)
    throw err
  }
}

async function removePost(postId, loggedInUser) {
  try {
    if (!loggedInUser) throw new Error('Must be logged in to remove a post')
    if (!isAdminOrEditor(loggedInUser)) {
      throw new Error(
        'Permission denied – only admins or editors may remove posts'
      )
    }

    await postService.remove(postId)
    socketService.emit('post-removed', postId)
    return postId
  } catch (err) {
    console.error('Error in removePost:', err)
    throw err
  }
}

// --------------------------------------------------
// 3. LIKE / HELPFUL
// --------------------------------------------------
export async function toggleLike(postId, loggedInUserId) {
  try {
    if (!loggedInUserId) throw new Error('User ID is required to toggle like')

    const post = await postService.getById(postId)
    if (!post) throw new Error(`Post with id ${postId} not found`)

    const isLiked = post.likes.includes(loggedInUserId)
    post.likes = isLiked
      ? post.likes.filter((id) => id !== loggedInUserId)
      : [...post.likes, loggedInUserId]

    await postService.save({ _id: postId, likes: post.likes })

    const fullPost = await postService.getById(postId) // ✅ שליפה מחדש עם commentCount
    return fullPost
  } catch (err) {
    console.error('Error in toggleLike:', err)
    throw err
  }
}
async function updateHelpful(postId, type, loggedInUser) {
  try {
    if (!['yes', 'no'].includes(type)) {
      throw new Error('Invalid type. Must be "yes" or "no".')
    }

    let userId = loggedInUser?._id
    if (!userId) {
      userId = utilService.getFromLocalStorage('anonymousId')
      if (!userId) {
        userId = `anon-${crypto.randomUUID()}`
        utilService.saveToLocalStorage('anonymousId', userId)
      }
    }

    const updatedPost = await postService.updateHelpful(postId, {
      type,
      userId,
    })
    socketService.emit('post-updated', updatedPost)
    return updatedPost
  } catch (err) {
    console.error('Error in updateHelpful:', err)
    throw err
  }
}
